/****************************************************
 * Module Name:  mixShapeMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import MixShapeProxy from "../../utility/proxy/manager/mixProxy/mixShapeProxy";
import AddMixShapeModal from "./addMixShapeModal";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class MixShapeMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      shapes: [],
      showAddShapeModal: false,

      shapeOptions: [],
      editOperation: false,
      sourceData: {},
      errorSnackbar: false,
      loadingButton: false,
      accesDenied: false,
      showShapeLoadSpinner: true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MIX_SHAPE
      ),
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("MIX_MASTER_SHAPE");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchShapeData(false);
    }
  }

  fetchShapeData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showShapeLoadSpinner: true });
    MixShapeProxy.GetAllShape((responseData) => {
      if (responseData) {
        this.setState({ shapes: responseData, showShapeLoadSpinner: false, shapeOptions: Utility.getDistinctOptions(responseData, 'name') })
      } else {
        this.setState({ errorSnackbar: true, showShapeLoadSpinner: false })
      }
    }
    );
  };

  initialColumns = () => [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      minWidth: 50,
    },
    {
      field: "name",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      minWidth: 140,
      flex: 1,
      type: "singleSelect",
      valueOptions: this.state.shapeOptions,
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "created",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 120,
      type: "date",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalDate(params.value),
    },
    {
      field: "description",
      headerAlign: "center",
      align: "center",
      headerName: "DESCRIP",
      flex: 1,
      minWidth: 150,
      type: "singleSelect",
      valueOptions: this.state.shapeOptions,
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "seq",
      headerName: "SEQUENCE",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: 'number',
      renderCell: renderCellExpand,
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      minWidth: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => {
              this.setState({
                showAddShapeModal: true,
                editOperation: true,
                sourceData: params.row,
                loadingButton: false,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddShapeModal = () => {
    this.setState({
      showAddShapeModal: false,
    });
  };

  handleValidation = (shapeData) => {
    //TODO do validation if needed here
    return true;
  };

  addShape = (shapeData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      //Updating Shape, and once shape is updated on call refresh the list of current shapes
      MixShapeProxy.UpdateShape(shapeData, (response) => {
        if (response) {
          this.fetchShapeData(true);
          //Hidding shape modal
          this.hideAddShapeModal();
          this.setState({ loadingButton: false });
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    } else {
      //Creating new Shape, and once shape is created on call refresh the list of current shapes
      MixShapeProxy.AddShape(shapeData, (response) => {
        if (response) {
          this.fetchShapeData(true);
          //Hidding shape modal
          this.hideAddShapeModal();
          this.setState({ loadingButton: false });
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    }
  };

  render() {
    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={{
            display: "flex",
            alignItems: "center",
            marginHorizontal: "5px",
            "&.MuiDataGrid-toolbarContainer": {
              padding: "0px 4px !important",
            },
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
          <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
          {this.state.showShapeLoadSpinner ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlinedIcon
                color="primary"
                fontSize="medium"
                onClick={() => this.fetchShapeData(true)}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          )}

          {Utility.isAddAllowed(this.state.local_Permission) ? (
            <Tooltip title="Add Shape">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                onClick={() =>
                  this.setState({
                    showAddShapeModal: true,
                    editOperation: false,
                    sourceData: {},
                  })
                }
                startIcon={<AddBoxOutlinedIcon size="large" />}
              >
                Shape
              </Button>
            </Tooltip>
          ) : null}

          <GridToolbarQuickFilter debounceMs={500} />
        </GridToolbarContainer>
      );
    }
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.shapes;
    const columns = Utility.isUpdateAllowed(this.state.local_Permission)
      ? this.initialColumns()
      : this.initialColumns().filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{Constants.APiFailErrorMessage}</Box>}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title="Failed"
        />
        <br />
        {this.state.showAddShapeModal ? (
          <AddMixShapeModal
            Show={true}
            handleClose={this.hideAddShapeModal}
            handleSave={this.addShape}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "85vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },

                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              slots={{ toolbar: CustomToolbar }}
              rows={rows}
              columns={columns}
              density="compact"
              getRowId={(row) => row.id}
              loading={this.state.showShapeLoadSpinner}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    id: false,
                    created: false
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default MixShapeMaster;
